/**
 * @fileoverview Drawer that lists all tags with occurrence counts, search, and
 * sorting capabilities for tag management and navigation.
 * Uses DrawerContentWrapper for consistent loading/error/empty state handling.
 */

import { Check, Label, Search } from '@mui/icons-material';
import { IconButton, List, ListItem, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { PiSortDescending } from 'react-icons/pi';
import { useTagsWithOccurrence, type TagWithOccurrence } from '../hooks/useTagsWithOccurrence';
import type { TagsDrawerProps } from '../types';
import { DrawerContainer, SearchField } from '@/shared/components';
import { DRAWER_WIDTH } from '@/config/constants';
import { DrawerContentWrapper } from './DrawerContentWrapper';

type SortOption = 'a-z' | 'z-a' | 'freq-high' | 'freq-low';

export function TagsDrawer({ open, onClose, onTagClick }: TagsDrawerProps): React.JSX.Element {
  const { data: tags, isLoading, error } = useTagsWithOccurrence();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('freq-high');
  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Filter and sort tags
  const filteredAndSortedTags = useMemo(() => {
    if (!tags) return [];

    // Filter by search query
    let filtered = tags;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = tags.filter((tag) =>
        tag.name.toLowerCase().includes(query) ||
        tag.description?.toLowerCase().includes(query)
      );
    }

    // Sort based on selected option
    const sorted = [...filtered];
    switch (sortBy) {
      case 'a-z':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'z-a':
        sorted.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'freq-high':
        sorted.sort((a, b) => b.occurrence - a.occurrence || a.name.localeCompare(b.name));
        break;
      case 'freq-low':
        sorted.sort((a, b) => a.occurrence - b.occurrence || a.name.localeCompare(b.name));
        break;
    }

    return sorted;
  }, [tags, searchQuery, sortBy]);

  const handleTagClick = (tag: TagWithOccurrence) => {
    if (onTagClick) {
      onTagClick(tag.id!, tag.name);
    }
  };

  // Symmetric search state handlers: openSearch/closeSearch pair
  const openSearch = () => {
    setIsSearchOpen(true);
  };

  const closeSearch = () => {
    setSearchQuery('');
    setIsSearchOpen(false);
  };

  const handleSearchToggle = () => {
    if (isSearchOpen) {
      closeSearch();
    } else {
      openSearch();
    }
  };

  return (
    <DrawerContainer
      anchor='right'
      open={open}
      onClose={onClose}
      width={DRAWER_WIDTH.TAGS}
      topSpacing={5}
    >
      {/* Search and Sort Controls */}
      <Stack spacing={0.5} sx={{ mt: -0.5, mb: 1 }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              justifyContent: "center",
              alignItems: "center"
            }}>
            <Tooltip title="Sort">
              <IconButton
                onClick={(e) => setSortMenuAnchor(e.currentTarget)}
                size="small"
              >
                <PiSortDescending size={20} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Search">
              <IconButton
                onClick={handleSearchToggle}
                size="small"
              >
                <Search fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          {isSearchOpen && (
            <SearchField
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder='Search tags...'
              autoFocus
              fullWidth
              sx={{ mt: -4 }}
            />
          )}

          <Menu
            anchorEl={sortMenuAnchor}
            open={Boolean(sortMenuAnchor)}
            onClose={() => setSortMenuAnchor(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem
              onClick={() => {
                setSortBy('a-z');
                setSortMenuAnchor(null);
              }}
              sx={{ minWidth: 220 }}
            >
              <Stack
                direction='row'
                sx={{
                  justifyContent: 'space-between',
                  width: '100%'
                }}>
                <span>Tag name (A to Z)</span>
                {sortBy === 'a-z' && <Check fontSize='small' />}
              </Stack>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setSortBy('z-a');
                setSortMenuAnchor(null);
              }}
            >
              <Stack
                direction='row'
                sx={{
                  justifyContent: 'space-between',
                  width: '100%'
                }}>
                <span>Tag name (Z to A)</span>
                {sortBy === 'z-a' && <Check fontSize='small' />}
              </Stack>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setSortBy('freq-high');
                setSortMenuAnchor(null);
              }}
            >
              <Stack
                direction='row'
                sx={{
                  justifyContent: 'space-between',
                  width: '100%'
                }}>
                <span>Frequency (high to low)</span>
                {sortBy === 'freq-high' && <Check fontSize='small' />}
              </Stack>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setSortBy('freq-low');
                setSortMenuAnchor(null);
              }}
            >
              <Stack
                direction='row'
                sx={{
                  justifyContent: 'space-between',
                  width: '100%'
                }}>
                <span>Frequency (low to high)</span>
                {sortBy === 'freq-low' && <Check fontSize='small' />}
              </Stack>
            </MenuItem>
          </Menu>
        </Stack>
      {/* Tags List */}
      <DrawerContentWrapper<TagWithOccurrence>
        loading={isLoading}
        error={error instanceof Error ? error : error ? new Error('Failed to load tags') : null}
        data={filteredAndSortedTags}
        errorMessage="Failed to load tags"
        emptyState={{
          icon: <Label />,
          title: searchQuery ? 'No tags found' : 'No tags yet',
          subtitle: searchQuery ? 'Try a different search term' : 'Create tags to organize your notes',
        }}
        renderContent={(tagsList) => (
          <>
            <Typography
              variant='caption'
              sx={{
                color: 'text.secondary',
                mb: 1,
                display: 'block'
              }}>
              {tagsList.length} {tagsList.length === 1 ? 'tag' : 'tags'}
            </Typography>
            <List sx={{ p: 0 }}>
              {tagsList.map((tag) => (
                <div key={tag.id}>
                  <ListItem
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      cursor: onTagClick ? 'pointer' : 'default',
                      '&:hover': onTagClick ? { backgroundColor: 'action.hover' } : {},
                      borderRadius: 1,
                      px: 2,
                      py: 0.5,
                    }}
                    onClick={() => handleTagClick(tag)}
                  >
                    <Stack
                      direction='row'
                      sx={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        mb: tag.description ? 1 : 0
                      }}>
                      <Typography variant="body2" sx={{ ml: 0.5 }}>
                        {tag.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          minWidth: 50,
                          textAlign: 'right'
                        }}>
                        {tag.occurrence}
                      </Typography>
                    </Stack>
                    {tag.description && (
                      <Typography
                        variant='caption'
                        sx={{
                          color: 'text.secondary',
                          mt: 0.5
                        }}>
                        {tag.description}
                      </Typography>
                    )}
                  </ListItem>
                </div>
              ))}
            </List>
          </>
        )}
      />
    </DrawerContainer>
  );
}

